import { Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Camera, CameraNotification, Device, EldVehicle } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import { addDaysToDate, today } from "src/utils/date-utils";
import { useAuthContext } from "src/auth/hooks";
import NoneFoundReport from "src/components/none-found-report";
import { NoCamerasFound } from "../list";

type CameraWithDevice = Camera & {
  device?: Device
}

type SafetyInboxProps = {
  cameraNotifications?: CameraNotification[],
  onCameraNotificationSelected: (a: CameraNotification) => void
}

const SafetyInbox = memo(({ cameraNotifications, onCameraNotificationSelected }: SafetyInboxProps) => {
  const { sortedList, sortData } = useSort(cameraNotifications);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["location", "reason", "eldVehicleUnitNumber", "eldDriverFirstName", "eldDriverLastName"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell />

              <SortableHeader sortKey="eventDateTime" sortData={sortData} >
                Time
              </SortableHeader>

              <SortableHeader sortKey="durationSeconds" sortData={sortData} >
                Duration (sec)
              </SortableHeader>

              <SortableHeader sortKey="location" sortData={sortData} >
                Location
              </SortableHeader>

              <SortableHeader sortKey="reason" sortData={sortData} >
                Reason
              </SortableHeader>

              <SortableHeader sortKey="eldVehicleUnitNumber" sortData={sortData} >
                Vehicle
              </SortableHeader>

              <SortableHeader sortKey="eldDriverFirstName" sortData={sortData} >
                Driver
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((camera, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onCameraNotificationSelected(camera)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                <img src="" alt="" />
              </TruxTableCell>

              <TruxTableCell>
                {camera.eventDateTime}
              </TruxTableCell>

              <TruxTableCell>
                {camera.durationSeconds}
              </TruxTableCell>
              <TruxTableCell>
                {camera.location}
              </TruxTableCell>
              <TruxTableCell>
                {camera.reason}
              </TruxTableCell>
              <TruxTableCell>
                {camera.eldVehicleUnitNumber}
              </TruxTableCell>

              <TruxTableCell>
                {camera.eldDriverFirstName} {camera.eldDriverLastName}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});


type PageModel = {
  cameraNotifications?: CameraNotification[],
  cameras?: CameraWithDevice[],
  eldVehicles?: EldVehicle[],
}

export default function Page() {
  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const settings = useSettingsContext();
  const pageFilterData = usePageFilterLoaderData(
      addDaysToDate(today(timezone), -2),
      today(timezone)
    );

  const router = useRouter();
  const dataLoader = useCallback(() => ({
    cameras: axios.get(endpoints.cameras.findAll).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
    cameraNotifications: !pageFilterData.fromDate || !pageFilterData.fromDate
      ? []
      : axios.get(formatEndpoint(endpoints.cameraNotifications.findAll, {
                fromDate: pageFilterData.fromDate,
                toDate: pageFilterData.toDate,
              })).then(response => response.data),
  }), [pageFilterData]);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
    loaded,
    setShouldRefresh
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [selectedCamera, setSelectedCamera] = useState<CameraWithDevice>();

  const selectedVehicle = data?.eldVehicles?.find(eldVehilce => eldVehilce.deviceId === selectedCamera?.device?.id);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.cameras?.length !== 0 &&
          <>
            <PageHeader title="Safety Inbox" />
            <PageFilterLoader pageFilterData={pageFilterData} onRun={() => {
              setShouldRefresh(true);
            }} />
          </>
        }

        {data && data?.cameras?.length === 0 && <NoCamerasFound />}

        {data && data?.cameras?.length !== 0 && <>
          {loaded && data && data.cameraNotifications?.length === 0 ? <NoneFoundReport /> : <SafetyInbox cameraNotifications={data.cameraNotifications} onCameraNotificationSelected={(cameraNotification: CameraNotification) => {
            // setSelectedCamera({...camera});
            // setIsOpen(true);
          }} />}
        </>}

        {/* {isOpen && <SimpleDialog
            title="Assign Camera"
            open={isOpen}
            onCancel={() => setIsOpen(false)}
            onSubmit={() => {
              setDialogLoading(true);

              axios.post(endpoints.cameras.assign, {
                deviceId: selectedCamera?.device?.id || 0,
                cameraId: selectedCamera?.id || 0,
              }).then(() => {
                enqueueSnackbar("Successfully Assigned the Camera!");
                setShouldRefresh(true);
                setIsOpen(false);
              }).finally(() => {
                setDialogLoading(false);
              })
            }}
            submitDisabled={dialogLoading}
            submitButton="Assign Camera"
            submitButtonIcon={<Iconify icon="basil:save-solid" />}
            submitButtonColor="info">

            <TruxFormField label="Truck">
              <TruxAutocompleteField
                options={data?.eldVehicles?.map(eldVehicle => ({
                  label: `${eldVehicle.vehicleUnitNumber} - ${eldVehicle.vin}`,
                  id: eldVehicle.id
                })) || []}
                onClear={() => {
                  if (selectedCamera) {
                    setSelectedCamera({...selectedCamera, device: undefined});
                  }
                }}
                onChange={(value) => {
                  console.log(value)
                  if (selectedCamera) {
                    if (value == null) {
                      setSelectedCamera((current) => (!current ? current : {...current}));
                    } else {
                      data?.eldVehicles?.forEach(eldVehicle => {
                        if (value.id === eldVehicle.id) {
                          setSelectedCamera({...selectedCamera, device: {
                            id: eldVehicle.deviceId,
                            deviceType: `ELD Truck #${eldVehicle.vehicleUnitNumber}`
                          }as Device });
                        }
                      })
                    }
                  }
                }}
                selected={{
                  id: selectedVehicle ? selectedVehicle.id : 0,
                  label: selectedVehicle ? `${selectedVehicle.vehicleUnitNumber} - ${selectedVehicle.vin}` : `Not Assigned`,
                }}
              />
            </TruxFormField>
          </SimpleDialog>
        } */}
      </DisplayPageData>
    </Container>
  );
}
